import type { Address } from '#app/broker/trucklist/types'
import { addHours, addMinutes, format, set } from 'date-fns'
import { formatInTimeZone, fromZonedTime, toZonedTime } from 'date-fns-tz'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

// Type for Maybe utility
type Maybe<T> = T | null | undefined

dayjs.extend(utc)
dayjs.extend(timezone)

export const ShortDateFormat = 'M/d/yy'
export const LongDateFormat = 'MM/dd/yyyy'
export const ShortDateTimeZoneFormat = 'M/d/yy h:mm a (z)'
export const SentenceDateFormat = 'EEE, MMM d, yyyy'
export const ShortTimeZoneFormat = 'h:mm a (z)'

/**
 * Return the browser's local time zone.
 * @returns
 */
export const getLocalTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

export const convertToUTC = (date: string, time: string, timeZone: string) => {
  // Combine date and time into a single string
  const dateTimeString = `${date} ${time}`

  const utcDate = fromZonedTime(dateTimeString, timeZone)

  return utcDate
}

export const convertFromUTC = (dateTime: string, timeZone?: string) => {
  const date = fromZonedTime(dateTime, 'UTC')
  const zonedDate = toZonedTime(date, timeZone || getLocalTimeZone())
  return zonedDate
}

/**
 * Extracts and formats the date and time from a Date object.
 *
 * @param date - The Date object to extract from.
 * @returns An object containing formatted date and time strings.
 */
export function getDateTimeComponents(date: Date): {
  formattedDate: string
  formattedTime: string
} {
  const year = date.getUTCFullYear()
  const month = date.getUTCMonth() + 1 // Months are zero-indexed, so we add 1
  const day = date.getUTCDate()

  const hours = date.getUTCHours()
  const minutes = date.getUTCMinutes()
  const seconds = date.getUTCSeconds()

  const formattedDate = `${year}-${String(month).padStart(2, '0')}-${
    String(
      day
    ).padStart(2, '0')
  }`
  const formattedTime = `${String(hours).padStart(2, '0')}:${
    String(
      minutes
    ).padStart(2, '0')
  }:${String(seconds).padStart(2, '0')}`

  return { formattedDate, formattedTime }
}

export const convertFromUTCString = (dateTime: string, timeZone: string) => {
  const utcDate = new Date(dateTime)
  const zonedDate = toZonedTime(utcDate, timeZone)
  return zonedDate
}

/**
 * Adjusts the date to GMT
 */
export const adjustDateToGmt = (date: Date) => {
  const tzOffset = date.getTimezoneOffset() / 60
  date.setHours(date.getHours() - tzOffset)
  return date
}

export const formatDateTime = (
  date: string,
  time: string | string[] | Date
) => {
  if (!date || !time) return null
  const timeStr = Array.isArray(time) ? time[0] : time
  if (!timeStr || timeStr === 'Invalid Date' || timeStr instanceof Date) {
    return null
  }
  const formattedTime = timeStr.includes(':') ? timeStr : `${timeStr}:00`
  return new Date(`${date}T${formattedTime}`)
}

export const formatTimeZoneAbbr = (address?: Address) => {
  return address?.timeZoneName
    ?.split(' ')
    .map((word) => word[0])
    .join('')
}

/**
 * Checks if a date string is within the last week.
 * @param dateString - The date string to check.
 * @returns A boolean indicating whether the date is within the last week.
 */
export const isWithinLastWeek = (dateString: string): boolean => {
  const date = new Date(dateString)
  const now = new Date()
  const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
  return date >= weekAgo && date <= now
}

/**
 * Formats a date string to the format MM/DD/YYYY
 * @param date - The date string to format
 * @returns The formatted date string
 */
export const formatDateToSlashFormat = (date: string) => {
  const dateObj = new Date(date)
  const month = (dateObj.getUTCMonth() + 1).toString().padStart(2, '0')
  const day = dateObj.getUTCDate().toString().padStart(2, '0')
  const year = dateObj.getUTCFullYear()
  return `${month}/${day}/${year}`
}

/**
 * Formats a date string to the format MMM D - hh:mm A in UTC
 * @param dateString - The date string to format
 * @returns The formatted date string
 */
export const formatDateLong = (
  dateString: string | null | undefined
): string => {
  if (!dateString) return '-'

  const date = dayjs(dateString)
  if (!date.isValid()) return '-'

  return date.format('MMM D - hh:mm A')
}

export const formatDateLongStandard = (
  dateString: string | null | undefined
): string => {
  if (!dateString) return '-'
  const date = dayjs(dateString)
  if (!date.isValid()) return '-'

  return date.utc().format('MMM D, YYYY hh:mm A')
}

/**
 * Formats a date string to the format MMM D, YYYY
 * @param dateString - The date string to format
 * @returns The formatted date string
 */
export const formatDateShort = (
  dateString: string | null | undefined
): string => {
  if (!dateString) return '-'

  const date = dayjs(dateString)
  if (!date.isValid()) return '-'

  return date.format('MMM D, YYYY')
}

export const getNextAvailablePickupTime = () => {
  const now = new Date()

  // Add 1 minute to ensure we're in the future
  const futureDate = addMinutes(now, 1)

  // Get minutes and round up to next 15 minute interval
  const minutes = futureDate.getMinutes()
  const roundedMinutes = Math.ceil(minutes / 15) * 15

  // Create new date with rounded minutes
  const roundedDate = set(futureDate, {
    minutes: roundedMinutes,
    seconds: 0,
    milliseconds: 0
  })

  return format(roundedDate, "yyyy-MM-dd'T'HH:mm")
}

export const getDefaultDropoffTime = (
  pickupDateTime: string,
  hoursAfterPickup = 4
) => {
  if (!pickupDateTime) return ''

  const pickupDate = new Date(pickupDateTime)
  if (Number.isNaN(pickupDate.getTime())) return ''

  const dropoffDate = addHours(pickupDate, hoursAfterPickup)

  return format(dropoffDate, "yyyy-MM-dd'T'HH:mm")
}

export interface DateTimeResult {
  date: string
  time: string
  dateEnd: string | null
  timeEnd: string | null
}

export const convertPartsToAgnosticTimeString = (
  date: string,
  time: string
) => {
  return `${date} ${time}`
}

/**
 * Converts date and time to UTC and returns formatted components
 * @param date - The base date
 * @param time - The base time
 * @param timeZoneId - The timezone identifier
 * @param timeEnd - Optional end time
 * @returns Formatted date and time components
 */
export const convertToUTCDateParts = (
  date: string,
  time: string,
  timeZoneId: string,
  timeEnd?: string | null
): DateTimeResult => {
  const dateTimeUTC = convertToUTC(date, time, timeZoneId)
  const { formattedDate, formattedTime } = getDateTimeComponents(dateTimeUTC)

  let endDate = null
  let endTime = null

  if (timeEnd && timeEnd.trim() !== '') {
    const dateTimeEndUTC = convertToUTC(date, timeEnd, timeZoneId)
    const components = getDateTimeComponents(dateTimeEndUTC)
    endDate = components.formattedDate
    endTime = components.formattedTime
  }

  return {
    date: formattedDate,
    time: formattedTime,
    dateEnd: endDate,
    timeEnd: endTime
  }
}
/**
 * @description Parses the date string from the format example "Wed, Jun 26, 2024" to a Date object
 * @param dateString - The date string
 * @returns The parsed date
 */

export function parseDate(dateString: string): Date {
  const [, month, day, year] = dateString.split(' ')
  const monthIndex = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec'
  ].indexOf(month)
  return new Date(Number.parseInt(year), monthIndex, Number.parseInt(day))
}

/**
 * Generic date formatting function with timezone support
 */
export const formatDateGeneric = (
  date: string | Date | undefined | null,
  formatString: string,
  timezone?: string,
  fromUTC = false
): string => {
  if (!date) return '--'

  const dateObj = typeof date === 'string' ? new Date(date) : date
  if (!dateObj || Number.isNaN(dateObj.getTime())) return '--'

  if (timezone) {
    if (fromUTC) {
      // Convert from UTC to specified timezone
      return formatInTimeZone(dateObj, timezone, formatString)
    }
    // Convert from local time to specified timezone
    const utcDate = fromZonedTime(dateObj, 'UTC')
    return formatInTimeZone(utcDate, timezone, formatString)
  }

  // No timezone specified, use local formatting
  return format(dateObj, formatString)
}

/**
 * Format date for display with timezone support
 */
export const formatDateForDisplay = (
  date?: string | Maybe<string>,
  timezone?: string | Maybe<string>
): string => {
  if (!date) {
    return '--'
  }
  const dateString = timezone
    ? formatDateShortMonthFullYearFromUTC(date, timezone)
    : formatDateShortMonthFullYear(date)

  const timeString = timezone
    ? formatTimeForDisplay(date, timezone)
    : formatTimeForDisplay(date)

  if (!timeString) {
    return dateString
  }

  return `${dateString} - ${timeString}`
}

/**
 * Format date to MMM DD, YYYY from UTC with timezone conversion
 */
export const formatDateShortMonthFullYearFromUTC = (
  date: string | Date | undefined | null,
  timezone: string
): string => {
  const format = 'MMM dd, yyyy'
  return formatDateGeneric(date, format, timezone, true)
}

/**
 * Format date to MMM DD, YYYY
 */
export const formatDateShortMonthFullYear = (
  date: string | Date | undefined | null,
  timezone?: string
): string => {
  const format = 'MMM dd, yyyy'
  return formatDateGeneric(date, format, timezone)
}

/**
 * Format time for display with timezone support
 */
export const formatTimeForDisplay = (
  date?: string | Maybe<string>,
  timezone?: string | Maybe<string>,
  showMidnight = false
): string => {
  if (!date) {
    return '--'
  }
  return timezone
    ? parseTimeFromUTC(date, timezone, showMidnight)
    : parseTime(date, showMidnight)
}

/**
 * Parse time to correct time format
 * @param date
 * @param showMidnight - if true, show '00:00' instead of ''
 * @returns string like '12:00'
 */
export const parseTime = (
  date: Date | undefined | string,
  showMidnight = false
): string => {
  if (!date) {
    return 'No Date/Time'
  }
  const dayjsDate = dayjs(date)
  if (dayjsDate.hour() === 0 && dayjsDate.minute() === 0 && !showMidnight) {
    return '' // Return an empty string if it's midnight
  }

  return dayjsDate.format('h:mm A')
}

/**
 * Parse time to correct time format with timezone conversion
 * @param date
 * @param timezone
 * @param showMidnight - if true, show '00:00' instead of ''
 * @returns string like '12:00'
 */
export const parseTimeFromUTC = (
  date: Date | undefined | string,
  timezone?: string,
  showMidnight = false
): string => {
  const tz = timezone || getLocalTimeZone()
  if (!date) {
    return 'No Date/Time'
  }
  const dayjsDate = dayjs.utc(date).tz(tz)
  // Check if the time is midnight (00:00)
  if (dayjsDate.hour() === 0 && dayjsDate.minute() === 0 && !showMidnight) {
    return '' // Return an empty string if it's midnight
  }
  return dayjsDate.format('h:mm A z')
}
